import { format } from 'date-fns';
import { Edit2, Eye, FileText, FolderOpen, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useDeleteTransaction, useTransactions } from '../hooks/useClosingDisclosureQueries';

export default function CDDraftsListPage() {
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const { setPageHeader, clearPageHeader } = usePageHeader();

  // Use TanStack Query hook for fetching transactions
  const { data, isLoading, error: fetchError } = useTransactions({ status: 'draft', limit: 50 });

  // Use mutation hook for deleting transactions
  const deleteTransactionMutation = useDeleteTransaction();

  // Set page header
  useEffect(() => {
    setPageHeader({
      title: 'Closing Disclosure Drafts',
      breadcrumbs: [{ label: 'Dashboard', href: '/dashboard' }, { label: 'Closing Disclosures' }],
    });
    return () => clearPageHeader();
  }, [setPageHeader, clearPageHeader]);

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this draft?')) return;

    setDeleteError(null);
    try {
      await deleteTransactionMutation.mutateAsync(id);
      // No need to update local state - TanStack Query will invalidate and refetch
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete draft';
      setDeleteError(errorMessage);
    }
  };

  // Derive transactions from query data
  const transactions = data?.transactions ?? [];
  const error = fetchError?.message || deleteError;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Closing Disclosure Drafts</h1>
          <p className="text-muted-foreground">Manage your draft closing disclosures</p>
        </div>
        <Link to="/dashboard/create-cd">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Draft
          </Button>
        </Link>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : transactions.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No drafts yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first closing disclosure draft to get started.
              </p>
              <Link to="/dashboard/create-cd">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Draft
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Drafts ({transactions.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      Property Address
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      File Number
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      Sale Price
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">
                      Last Modified
                    </th>
                    <th className="text-right py-3 px-4 font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((transaction) => (
                    <tr key={transaction.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div className="font-medium">{transaction.property_address || '-'}</div>
                        {transaction.closing_date && (
                          <div className="text-sm text-muted-foreground">
                            Closing: {format(new Date(transaction.closing_date), 'MMM d, yyyy')}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {transaction.file_number || '-'}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {transaction.sale_price
                          ? new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD',
                              minimumFractionDigits: 0,
                            }).format(transaction.sale_price)
                          : '-'}
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {transaction.updated_at
                          ? format(new Date(transaction.updated_at), 'MMM d, yyyy h:mm a')
                          : '-'}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end gap-2">
                          <Link to={`/dashboard/cd/${transaction.id}/edit`}>
                            <Button variant="ghost" size="sm" title="Edit">
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/cd/${transaction.id}/documents`}>
                            <Button variant="ghost" size="sm" title="Documents">
                              <FolderOpen className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Link to={`/cd/${transaction.id}/preview`}>
                            <Button variant="ghost" size="sm" title="Preview">
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="sm"
                            title="Delete"
                            onClick={() => handleDelete(transaction.id!)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
